import { FC, useEffect, useRef } from 'react';
import stylex from '@stylexjs/stylex';
import { MainGuestFromFormBody } from '../MainGuestFromFormBody';
import { AdditionalGuestsFromFormBody } from '../AdditionalGuestsFromFormBody';
import { RadiobuttonsFromFormBody } from '../RadiobuttonsFromFormBody';
import { CheckboxesFromFormBody } from '../CheckboxesFromFormBody';
import { getGuestsFromBodyForm } from '../lib/getMainGuestFromForm';
import { getRadiobuttonsFromBodyForm } from '../lib/getRadiobuttonsFromBodyForm';
import { getCheckboxesFromBodyForm } from '../lib/getCheckboxesFromBodyForm';
import {
  mapGuestsToSubmissionData,
  type Form,
  type GuestInfo,
  type Guests,
  type RadiobuttonSubmissionData,
  type CheckboxesSubmissionData,
} from '@entities/leadCollector';
import { styles } from '../MainGuestTab/styles';

export interface FormBodyWithGuestsProps {
  formData: Form;
  mainGuest: GuestInfo;
  registerValidate?: (validate: () => Promise<boolean>) => void;
  registerCollectSubmissionInfo?: (collect: () => string | null) => void;
}

export const FormBodyWithGuests: FC<FormBodyWithGuestsProps> = ({
  formData,
  mainGuest,
  registerValidate,
  registerCollectSubmissionInfo,
}) => {
  const mainValidateRef = useRef<(() => Promise<boolean>) | null>(null);
  const extraValidateRef = useRef<(() => Promise<boolean>) | null>(null);
  const radiobuttonsValidateRef = useRef<(() => Promise<boolean>) | null>(null);
  const checkboxesValidateRef = useRef<(() => Promise<boolean>) | null>(null);
  const mainGuestRef = useRef<GuestInfo | null>(null);
  const adultGuestsRef = useRef<GuestInfo[]>([]);
  const childrenGuestsRef = useRef<GuestInfo[]>([]);
  const radiobuttonsRef = useRef<RadiobuttonSubmissionData[]>([]);
  const checkboxesRef = useRef<CheckboxesSubmissionData[]>([]);

  const maxExtraAdults = formData.maxExtraAdults ?? 0;
  const maxExtraChildren = formData.maxExtraChildren ?? 0;
  const showExtraBlock = !!maxExtraAdults || !!maxExtraChildren;

  const guests = getGuestsFromBodyForm(formData.bodyForm);
  const adultTemplate = guests?.adultGuest?.[0] ?? null;
  const childrenTemplate = guests?.childrenGuest?.[0] ?? null;
  const radiobuttonGroups = getRadiobuttonsFromBodyForm(formData.bodyForm);
  const checkboxGroups = getCheckboxesFromBodyForm(formData.bodyForm);

  useEffect(() => {
    if (!showExtraBlock) extraValidateRef.current = null;
  }, [showExtraBlock]);

  useEffect(() => {
    if (!registerValidate) return;
    const combined: () => Promise<boolean> = async () => {
      const mainValid = await mainValidateRef.current?.();
      if (mainValid === false) return false;
      const extraValid = await extraValidateRef.current?.();
      if (extraValid === false) return false;
      const radiobuttonsValid = await radiobuttonsValidateRef.current?.();
      if (radiobuttonsValid === false) return false;
      const checkboxesValid = await checkboxesValidateRef.current?.();
      return checkboxesValid !== false;
    };
    registerValidate(combined);
    return () => registerValidate(undefined as unknown as () => Promise<boolean>);
  }, [registerValidate]);

  useEffect(() => {
    if (!registerCollectSubmissionInfo) return;
    const collect = (): string | null => {
      if (!mainGuestRef.current) return null;
      const guestsToSubmit: Guests = {
        mainGuest: mainGuestRef.current,
        adultGuest: adultGuestsRef.current,
        childrenGuest: childrenGuestsRef.current,
      };
      const submissionData = mapGuestsToSubmissionData(guestsToSubmit);
      const radiobuttons = radiobuttonsRef.current;
      const payload = {
        ...submissionData,
        ...(radiobuttons.length ? { radiobuttons } : {}),
        ...(checkboxesRef.current.length ? { checkboxes: checkboxesRef.current } : {}),
      };
      return JSON.stringify(payload);
    };
    registerCollectSubmissionInfo(collect);
    return () => registerCollectSubmissionInfo(undefined as unknown as () => string | null);
  }, [registerCollectSubmissionInfo]);

  return (
    <>
      <MainGuestFromFormBody
        mainGuest={mainGuest}
        registerValidate={(fn) => {
          mainValidateRef.current = fn ?? null;
        }}
        onChange={(guest) => {
          mainGuestRef.current = guest;
        }}
      />
      {showExtraBlock && (
        <AdditionalGuestsFromFormBody
          maxExtraAdults={maxExtraAdults}
          maxExtraChildren={maxExtraChildren}
          adultTemplate={adultTemplate}
          childrenTemplate={childrenTemplate}
          adultGuestTitle={guests?.adultGuestTitle}
          childGuestTitle={guests?.childGuestTitle}
          adultButtonTitle={guests?.adultButtonTitle}
          childButtonTitle={guests?.childButtonTitle}
          registerValidate={(fn) => {
            extraValidateRef.current = fn ?? null;
          }}
          onChangeGuests={(adultGuests, childrenGuests) => {
            adultGuestsRef.current = adultGuests;
            childrenGuestsRef.current = childrenGuests;
          }}
        />
      )}
      {!!radiobuttonGroups?.length && (
        <section {...stylex.props(styles.section, styles.sectionDeliveryMethod)}>
          <RadiobuttonsFromFormBody
            groups={radiobuttonGroups}
            onChange={(radiobuttons) => {
              radiobuttonsRef.current = radiobuttons;
            }}
            registerValidate={(fn) => {
              radiobuttonsValidateRef.current = fn ?? null;
            }}
          />
        </section>
      )}
      {!!checkboxGroups?.length && (
        <section {...stylex.props(styles.section, styles.sectionDeliveryMethod)}>
          <CheckboxesFromFormBody
            groups={checkboxGroups}
            onChange={(checkboxes) => {
              checkboxesRef.current = checkboxes;
            }}
            registerValidate={(fn) => {
              checkboxesValidateRef.current = fn ?? null;
            }}
          />
        </section>
      )}
    </>
  );
};
